import { useContext, useEffect, useRef, useState } from 'react';

import { Form } from 'antd';
import { FollowUpContext } from 'fragments/follow-up-card/follow-up.context';

import { FollowUpCardPaymentController } from 'fragments/follow-up-card/follow-up-card-payment/interfaces';
import { RentalPaymentService, useApiRentalPaymentService } from 'services/rentalPayments/rentalPayment.service';
import { FollowUpService, useApiFollowUpService } from 'services/followUps/followUp.service';
import {
    ChargeDecisionResponseDto,
    FollowUpPaymentStatusEnum,
    UpdatePriceResponseDto,
} from 'services/followUps/followUp.dto';
import { useAPIMembershipService } from 'services/memberships/memberships.service';
import { useMessenger } from 'tools/view-hooks/messenger-hook';
import { ContractsService, useAPIContractService } from 'services/contracts/contracts.service';
import { currencyFormatter } from 'components/currency-input/currency-input.component';
import { calculateFollowUpRentUpdateNotice } from 'tools/rent-updates/rent-update-notice';
import { addMonetary, isMonetaryAmount, parseArsAmount } from 'tools/currency/ars-amount';
import { runFollowUpPaymentMutation } from 'services/followUps/follow-up-payment-coordinator';

// The recalculated mora is usable only as a real decimal(10,2) figure — finite, nonnegative, within
// range and within precision. Zero is valid (an overdue follow-up may simply owe no mora). Validation
// is on the RAW field — never `Number(...)` first — so null, '', false, numeric strings, oversized and
// over-precise values can never be coerced into a spurious 0/number and slip through. The mora is never
// computed on this side: it is read from the backend response, which recalculates it on the persisted
// rent and marks the result authoritative with `priceUpdated: true`.
const isUsableInterest = (value: unknown): value is number => isMonetaryAmount(value);

// The authoritative rent is trustworthy only as a real, strictly positive decimal(10,2) figure. Absent,
// malformed, zero/negative, oversized or over-precise values are rejected; the client never normalizes
// a value that could then disagree with persistence.
const isAuthoritativeRent = (value: unknown): value is number => isMonetaryAmount(value) && value > 0;

// A dontCharge echoed by the update response must be a raw boolean. A string such as "false" (or any
// non-boolean) must never be accepted — it could otherwise bypass the interest confirmation. Absent
// (undefined) is fine: the current waived state is kept.
const isValidResponseDontCharge = (value: unknown): boolean => value === undefined || typeof value === 'boolean';

const isAuthoritativePaymentStatus = (value: unknown): value is FollowUpPaymentStatusEnum =>
    value === FollowUpPaymentStatusEnum.COMPLETE ||
    value === FollowUpPaymentStatusEnum.PARTIAL ||
    value === FollowUpPaymentStatusEnum.INCOMPLETE ||
    value === FollowUpPaymentStatusEnum.UNKNOWN;

const isAuthoritativeFollowUpStatus = (
    paymentStatus: FollowUpPaymentStatusEnum,
    followUpStatus: unknown,
): followUpStatus is 'isPaid' | 'expired' | 'pending' =>
    paymentStatus === FollowUpPaymentStatusEnum.COMPLETE
        ? followUpStatus === 'isPaid'
        : followUpStatus === 'expired' || followUpStatus === 'pending';

// The price-update response is trusted atomically: payment status and amount must describe the same
// state against the newly authoritative rent/mora total. The raw amount is validated before any
// comparison, so coercible strings and out-of-range/over-precise values fail closed.
const isAuthoritativeUpdate = (
    dto: UpdatePriceResponseDto,
    expectedId: number | undefined,
    followUpStatus: string | undefined,
    currentDontCharge: boolean,
): dto is UpdatePriceResponseDto & {
    id: number;
    paymentStatus: FollowUpPaymentStatusEnum;
    rentalPrice: number;
    interestPrice: number;
    amountPaid: number;
    priceUpdated: true;
} => {
    if (
        dto.id !== expectedId ||
        !isAuthoritativePaymentStatus(dto.paymentStatus) ||
        dto.priceUpdated !== true ||
        !isAuthoritativeRent(dto.rentalPrice) ||
        !isUsableInterest(dto.interestPrice) ||
        !isValidResponseDontCharge(dto.dontCharge) ||
        !isMonetaryAmount(dto.amountPaid)
    ) {
        return false;
    }
    const dontCharge = dto.dontCharge ?? currentDontCharge;
    const total =
        followUpStatus === 'expired' && !dontCharge ? addMonetary(dto.rentalPrice, dto.interestPrice) : dto.rentalPrice;
    if (!isMonetaryAmount(total)) return false;
    const expectedStatus =
        dto.amountPaid === 0
            ? FollowUpPaymentStatusEnum.INCOMPLETE
            : dto.amountPaid >= total
            ? FollowUpPaymentStatusEnum.COMPLETE
            : FollowUpPaymentStatusEnum.PARTIAL;
    return dto.paymentStatus === expectedStatus;
};

const isAuthoritativeChargeDecision = (
    dto: ChargeDecisionResponseDto,
    expectedId: number | undefined,
    expectedDontCharge: boolean,
    expectedTotalDue?: number | null,
    requireCompletePayment = false,
): dto is ChargeDecisionResponseDto & {
    id: number;
    paymentStatus: FollowUpPaymentStatusEnum;
    dontCharge: boolean;
    amountPaid: number;
    followUpStatus: 'isPaid' | 'expired' | 'pending';
    chargeDecisionApplied: true;
} =>
    Boolean(dto) &&
    typeof dto === 'object' &&
    !Array.isArray(dto) &&
    dto.chargeDecisionApplied === true &&
    dto.id === expectedId &&
    isAuthoritativePaymentStatus(dto.paymentStatus) &&
    dto.dontCharge === expectedDontCharge &&
    isMonetaryAmount(dto.amountPaid) &&
    isAuthoritativeFollowUpStatus(dto.paymentStatus, dto.followUpStatus) &&
    isMonetaryAmount(expectedTotalDue) &&
    dto.paymentStatus ===
        (dto.amountPaid === 0
            ? FollowUpPaymentStatusEnum.INCOMPLETE
            : dto.amountPaid >= expectedTotalDue
            ? FollowUpPaymentStatusEnum.COMPLETE
            : FollowUpPaymentStatusEnum.PARTIAL) &&
    (!requireCompletePayment ||
        (dto.paymentStatus === FollowUpPaymentStatusEnum.COMPLETE && dto.amountPaid >= expectedTotalDue));

export const useFollowUpCardPaymentController = (
    followUpService: FollowUpService = useApiFollowUpService(),
    contractsService: ContractsService = useAPIContractService(),
    rentalPaymentService: RentalPaymentService = useApiRentalPaymentService(),
    messenger = useMessenger(),
): FollowUpCardPaymentController => {
    /* State */
    const [formInstance] = Form.useForm();
    const [rentalForm] = Form.useForm();
    const { followUp, setFollowUp, canPay } = useContext(FollowUpContext);
    const [isUpdateModalFormVisible, setIsUpdateModalFormVisible] = useState(false);
    const [isUpdateFormLoading, setIsUpdateFormLoading] = useState(false);
    const [isInterestModalVisible, setIsInterestModalVisible] = useState(false);
    const [valueInput, setValueInput] = useState('');
    // Same-session override for the "needs a price update" notice: the update endpoint's response may
    // not echo back a recalculated contract.rentalUpdates entry, which is what the notice itself keys
    // off. Setting this the moment a price update actually succeeds is what reliably unblocks "Pagar"
    // afterward, instead of depending on a shape this component has no control over.
    const [priceJustUpdated, setPriceJustUpdated] = useState(false);
    // Set only when "Pagar" itself redirected the user to the update modal, so the payment flow (typed
    // amount / interest decision / direct pay) resumes automatically once the price is confirmed —
    // opening the modal from the standalone "Actualizar" link must never trigger a payment on its own.
    const [pendingPayAfterUpdate, setPendingPayAfterUpdate] = useState(false);
    const paymentActionPointerDownRef = useRef(false);

    // Whether interest is currently waived, read straight off the follow-up instead of a local copy:
    // a separate useState here was the actual bug — it never learned about changes made anywhere else
    // (reverting to unpaid, the confirm-on-pay modal), so it could show one thing while the amount on
    // file reflected another. Interpreted STRICTLY as a raw boolean: any non-`true` runtime value
    // (a string "false"/"true", null, undefined, a number) collapses to safe `false`, so a malformed
    // loaded flag can never be truthy and silently bypass the interest confirmation.
    const dontCharge = followUp.dontCharge === true;

    // Payment status never locks this field: paid or not, the amount stays editable, and editing it
    // is itself how a paid follow-up gets its registered amount corrected. Only the plan's own
    // payment gate (unrelated to this follow-up's status) may disable it.
    const isInputDisabled = !canPay;

    /* Listeners */
    useEffect(() => {
        rentalForm.resetFields();
    }, [followUp.amountPaid]);

    /* View Events */

    const runPaymentMutationIntent = async (intent: () => Promise<boolean>): Promise<boolean> => {
        return runFollowUpPaymentMutation([followUp.id ?? Number.NaN], intent);
    };

    // Private endpoint helper for the current lock owner. Returning true requires both endpoint execution
    // and an identity/status-valid authoritative response; network and response failures stay false.
    const executePayment = async (amount: number, totalDue: number | null): Promise<boolean> => {
        if (!isMonetaryAmount(amount) || !isMonetaryAmount(totalDue)) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return false;
        }
        const paymentInput = { idFollowUp: followUp.id, amount };
        try {
            const dto = await rentalPaymentService.onPaymentChange(paymentInput);
            const expectedStatus = !isMonetaryAmount(dto.amountPaid)
                ? null
                : dto.amountPaid === 0
                ? FollowUpPaymentStatusEnum.INCOMPLETE
                : dto.amountPaid >= totalDue
                ? FollowUpPaymentStatusEnum.COMPLETE
                : FollowUpPaymentStatusEnum.PARTIAL;
            if (
                dto.id !== followUp.id ||
                expectedStatus === null ||
                dto.amountPaid !== amount ||
                dto.paymentStatus !== expectedStatus ||
                !isAuthoritativeFollowUpStatus(dto.paymentStatus, dto.followUpStatus)
            ) {
                messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
                return false;
            }
            const { id, paymentStatus, amountPaid, followUpStatus } = dto;
            setFollowUp((prevState) => ({
                ...prevState,
                id,
                paymentStatus,
                amountPaid,
                followUpStatus,
            }));
            return true;
        } catch (_error) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return false;
        }
    };

    const onPayRentFormSubmit = async (amount: number): Promise<boolean> => {
        if (!hasValidLoadedRentalPrice || !hasValidLoadedInterest) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return false;
        }
        const interestApplies = followUp.followUpStatus === 'expired' && !dontCharge;
        const totalDue = interestApplies ? addMonetary(rentalPrice, interest) : rentalPrice;
        return runPaymentMutationIntent(() => executePayment(amount, totalDue));
    };

    // Redirects a pay intent to the mandatory price update instead of paying: warns, arms the resume,
    // and opens the update modal. Shared by the red-icon "Pagar" and the Enter key so no entry point
    // can slip a payment past a pending update.
    const beginUpdateFirstFlow = () => {
        messenger.showErrorMessage({ key: 'follow-ups.must-update-price-first' });
        setPendingPayAfterUpdate(true);
        setIsUpdateModalFormVisible(true);
    };

    const onBlurPayRent = async (event: any) => {
        if (paymentActionPointerDownRef.current) {
            paymentActionPointerDownRef.current = false;
            return;
        }
        // A pending price update gates every payment path, this one included. Blurring the field is a
        // side effect of clicking "Pagar", so registering the old amount here would pay ahead of — and
        // then again after — the update. The explicit "Pagar" click is what arms and opens that flow.
        // A price update that failed closed leaves this gate up too, so nothing pays until a fresh,
        // trusted update actually clears it.
        if (paymentBlocked) {
            return;
        }
        // The field shows the es-AR formatted amount ("$ 1.234.567,89", NBSP-separated). Parse it with
        // the shared robust parser and FAIL CLOSED on anything malformed / non-finite / negative /
        // out of the decimal(10,2) range / over-precise — never coerce it to 0 and register a payment.
        // An empty field is not a valid amount either: blurring it registers nothing, so a following
        // "Pagar" click is what pays the full rental price.
        const amount = parseArsAmount(event?.target?.value);
        if (amount === null) {
            return;
        }
        await onPayRentFormSubmit(amount);
    };

    const onPaymentActionPointerDown = () => {
        paymentActionPointerDownRef.current = true;
    };

    const onPaymentActionPointerEnd = () => {
        paymentActionPointerDownRef.current = false;
    };

    const onEnterPayRent = async (inputs: any) => {
        // Enter is an explicit pay intent, so with a price update still pending it initiates the same
        // update-first flow as "Pagar" rather than paying the old amount.
        if (paymentBlocked) {
            beginUpdateFirstFlow();
            return;
        }
        const newAmount = inputs?.amountPaid;
        await onPayRentFormSubmit(newAmount);
    };

    // Shared by the direct "Pagar" click and by the same action resuming right after a price update:
    // takes the rent/interest/waived-flag as explicit arguments instead of reading the outer closure,
    // so a resume-after-update call is never able to compute a total against the price that was just
    // replaced — the mandatory order is update first, mora on the new amount second, never the reverse.
    const resolvePayment = async (
        currentRentalPrice: number,
        currentInterest: number,
        currentDontCharge: boolean,
        lockOwned = false,
    ): Promise<void> => {
        if (!hasValidLoadedRentalPrice || !hasValidLoadedInterest) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return;
        }
        // Loaded payment data and the form value stay on the raw trust boundary. Optional absence means
        // "nothing typed"; every present value must already be a decimal(10,2) runtime number.
        if (followUp.amountPaid !== undefined && !isMonetaryAmount(followUp.amountPaid)) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return;
        }
        const currentHasInterest = followUp.followUpStatus === 'expired' && currentInterest > 0;
        const currentTotalDue =
            currentDontCharge || !currentHasInterest
                ? currentRentalPrice
                : addMonetary(currentRentalPrice, currentInterest);
        if (currentTotalDue === null) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return;
        }
        const typedAmount = rentalForm.getFieldValue('amountPaid');
        if (typedAmount !== undefined && !isMonetaryAmount(typedAmount)) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return;
        }
        if (typedAmount > 0) {
            if (lockOwned) {
                await executePayment(typedAmount, currentTotalDue);
            } else {
                await onPayRentFormSubmit(typedAmount);
            }
            return;
        }
        if (currentHasInterest && !currentDontCharge) {
            setIsInterestModalVisible(true);
            return;
        }
        if (lockOwned) {
            await executePayment(currentTotalDue, currentTotalDue);
        } else {
            await onPayRentFormSubmit(currentTotalDue);
        }
    };

    // Quick action on the red icon. A scheduled increase that still needs its price confirmed must be
    // resolved first — paying now would register the old amount and price any mora off it — so this
    // redirects to the update action instead of paying, and lets onUpdateFormSubmit resume the payment
    // once that is actually done.
    const onMarkAsPaid = async () => {
        paymentActionPointerDownRef.current = false;
        if (
            !canPay ||
            (followUp.paymentStatus !== FollowUpPaymentStatusEnum.INCOMPLETE &&
                followUp.paymentStatus !== FollowUpPaymentStatusEnum.PARTIAL)
        ) {
            return;
        }
        if (paymentBlocked) {
            beginUpdateFirstFlow();
            return;
        }
        // The interest decision modal must appear on every state change that involves an overdue
        // follow-up with interest, regardless of whether dontCharge is currently true or false.
        // The user may want to reverse a previous waiver (or re-apply it), so the gate is
        // `hasInterest` alone — never gated by the current dontCharge value.
        if (hasInterest) {
            setIsInterestModalVisible(true);
            return;
        }
        resolvePayment(rentalPrice, interest, dontCharge);
    };

    const onInterestModalCancel = () => {
        setIsInterestModalVisible(false);
    };

    // First option on the confirm dialog: pay with interest included.
    // - If interest was not waived (dontCharge=false): pay the full rent + interest total directly.
    // - If interest was previously waived (dontCharge=true): reverse the waiver atomically and pay
    //   the full rent + interest total in one backend transaction, so the ledger stays consistent.
    const onPayWithInterest = async () => {
        setIsInterestModalVisible(false);
        if (dontCharge) {
            // Reverse the waiver and pay atomically via the charge-decision endpoint.
            const paymentIntent =
                followUp.paymentStatus === FollowUpPaymentStatusEnum.PARTIAL ? undefined : ('pay-rent' as const);
            await runChargeDecisionIntent(() => persistChargeDecision(false, paymentIntent));
            return;
        }
        // Exact integer-cents total; an overflow past the decimal(10,2) domain fails closed rather than
        // paying a wrong or over-precise amount.
        const total = !hasInterest ? rentalPrice : addMonetary(rentalPrice, interest);
        if (total === null) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return;
        }
        await onPayRentFormSubmit(total);
    };

    // Second option: one backend transaction waives interest and pays the authoritative rent. There is
    // deliberately no follow-up rental-payment request: a browser lock cannot make two HTTP writes atomic.
    const runChargeDecisionIntent = (intent: () => Promise<boolean>): Promise<boolean> =>
        runPaymentMutationIntent(intent);

    const onIgnoreInterestAndPay = async (): Promise<boolean> => {
        setIsInterestModalVisible(false);
        const paymentIntent =
            followUp.paymentStatus === FollowUpPaymentStatusEnum.PARTIAL ? undefined : ('pay-rent' as const);
        return runChargeDecisionIntent(() => persistChargeDecision(true, paymentIntent));
    };

    // Reverse action on the green icon: the same isPaid:false toggle the bulk "Marcar como no pagado"
    // action already sends, reused here for a single follow-up. The backend resets the amount to
    // whatever an unpaid follow-up carries, same as that existing action already does.
    const onMarkAsUnpaid = async (): Promise<boolean> => {
        paymentActionPointerDownRef.current = false;
        if (
            !canPay ||
            (followUp.paymentStatus !== FollowUpPaymentStatusEnum.COMPLETE &&
                followUp.paymentStatus !== FollowUpPaymentStatusEnum.PARTIAL)
        ) {
            return false;
        }
        return runPaymentMutationIntent(async () => {
            try {
                const response = await rentalPaymentService.addMultiplePayments([
                    { idFollowUp: followUp.id, isPaid: false },
                ]);
                if (!Array.isArray(response) || response.length !== 1) {
                    messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
                    return false;
                }
                const [dto] = response;
                if (
                    !dto ||
                    dto.id !== followUp.id ||
                    dto.paymentStatus !== FollowUpPaymentStatusEnum.INCOMPLETE ||
                    !isAuthoritativeFollowUpStatus(dto.paymentStatus, dto.followUpStatus) ||
                    !isMonetaryAmount(dto.amountPaid) ||
                    dto.amountPaid !== 0
                ) {
                    messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
                    return false;
                }
                setFollowUp((prevState) => ({
                    ...prevState,
                    id: dto.id,
                    paymentStatus: dto.paymentStatus,
                    amountPaid: dto.amountPaid,
                    followUpStatus: dto.followUpStatus,
                }));
                return true;
            } catch (_error) {
                messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
                return false;
            }
        });
    };

    const onUpdateFormSubmit = (inputs: any) => {
        // Validate before acquiring the coordinator so malformed input cannot occupy it.
        const rawPrice = inputs?.price;
        if (!isMonetaryAmount(rawPrice, { min: 0.01 })) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return;
        }
        const newAmount = rawPrice;
        void runPaymentMutationIntent(async () => {
            setIsUpdateFormLoading(true);
            try {
                const dto = await followUpService.updatePrice({ id: followUp.id, price: newAmount });
                const resuming = pendingPayAfterUpdate;
                setPendingPayAfterUpdate(false);

                if (!isAuthoritativeUpdate(dto, followUp.id, followUp.followUpStatus, dontCharge)) {
                    messenger.showErrorMessage({ key: 'follow-ups.missing-recalculated-interest' });
                    return false;
                }

                const authoritativeRent = dto.rentalPrice;
                const authoritativeInterest = dto.interestPrice;
                const updatedDontCharge = dto.dontCharge ?? dontCharge;
                setIsUpdateModalFormVisible(false);
                setPriceJustUpdated(true);
                setFollowUp((prevState) => ({
                    ...prevState,
                    id: dto.id,
                    paymentStatus: dto.paymentStatus,
                    amountPaid: dto.amountPaid,
                    rentalPrice: authoritativeRent,
                    interestPrice: authoritativeInterest,
                    dontCharge: updatedDontCharge,
                }));

                if (!resuming || dto.paymentStatus === FollowUpPaymentStatusEnum.COMPLETE) {
                    return true;
                }
                // This compound owner calls the private payment helper reentrantly while retaining
                // the one coordinator lock for the entire update-then-pay user intent.
                await resolvePayment(authoritativeRent, authoritativeInterest, updatedDontCharge, true);
                return true;
            } catch (_error) {
                setPendingPayAfterUpdate(false);
                messenger.showErrorMessage({ key: 'common-errors.web-error-message' });
                return false;
            } finally {
                setIsUpdateFormLoading(false);
            }
        });
    };
    const onClickEditButton = () => {
        setIsUpdateModalFormVisible(true);
    };
    const onUpdateFormCancel = () => {
        setIsUpdateModalFormVisible(false);
        setPendingPayAfterUpdate(false);
    };

    const onChangeInput = (event: any) => {
        const value = valueInput.concat(event.target.value);
        setValueInput(value);
    };

    // The server owns the full aggregate transition: it locks the authoritative row and persists the
    // decision plus any paid-ledger correction (or pay-rent intent) together. Only explicitly selected,
    // runtime-validated fields cross this response boundary into shared state.
    const persistChargeDecision = async (nextDontCharge: boolean, paymentIntent?: 'pay-rent') => {
        if (nextDontCharge === dontCharge && paymentIntent === undefined) {
            return true;
        }
        if (!isAuthoritativePaymentStatus(followUp.paymentStatus)) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return false;
        }
        const expectedPostDecisionTotal =
            !hasValidLoadedRentalPrice || !hasValidLoadedInterest
                ? null
                : followUp.followUpStatus === 'expired' && !nextDontCharge
                ? addMonetary(rentalPrice, interest)
                : rentalPrice;
        if (expectedPostDecisionTotal === null) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return false;
        }
        let resp: ChargeDecisionResponseDto | null = null;
        try {
            resp = await followUpService.applyChargeDecision(followUp.id ?? 0, {
                dontCharge: nextDontCharge,
                expectedPaymentStatus: followUp.paymentStatus,
                ...(paymentIntent ? { paymentIntent } : {}),
            });
        } catch (_error) {
            resp = null;
        }
        if (
            !resp ||
            !isAuthoritativeChargeDecision(
                resp,
                followUp.id,
                nextDontCharge,
                expectedPostDecisionTotal,
                paymentIntent === 'pay-rent',
            )
        ) {
            messenger.showErrorMessage({ key: 'follow-ups.payment-error' });
            return false;
        }
        const { id, paymentStatus, followUpStatus, dontCharge: authoritativeDontCharge, amountPaid } = resp;
        setFollowUp((prevState) => ({
            ...prevState,
            id,
            paymentStatus,
            followUpStatus,
            dontCharge: authoritativeDontCharge,
            amountPaid,
        }));
        return true;
    };

    const ignoreCharge = (event: any): Promise<boolean> =>
        runChargeDecisionIntent(() => persistChargeDecision(event.target.checked));

    const hasValidLoadedInterest = followUp.interestPrice === undefined || isMonetaryAmount(followUp.interestPrice);
    const hasValidLoadedRentalPrice = isMonetaryAmount(followUp.rentalPrice, { min: 0.01 });
    // interestPrice is optional in the loaded DTO; genuine absence means no interest. Any present value
    // must already be a trusted number. Invalid values are exposed as zero only for inert rendering and
    // are blocked before every action that could use them.
    const interest =
        followUp.interestPrice === undefined
            ? 0
            : isMonetaryAmount(followUp.interestPrice)
            ? followUp.interestPrice
            : 0;
    const rentalPrice = isMonetaryAmount(followUp.rentalPrice, { min: 0.01 }) ? followUp.rentalPrice : 0;
    // Whether there is an actual decision to make: no arrears, or an arrears follow-up that carries
    // no interest at all, never needs to ask — there is nothing to ignore.
    const hasInterest = followUp.followUpStatus === 'expired' && interest > 0;
    // Keep the displayed total on the same exact-cent path as payment submission. If either operand or
    // the sum is outside decimal(10,2), addMonetary fails closed and the returned view value falls back
    // to zero below instead of exposing an IEEE-noisy or out-of-domain amount.
    const realRentalPrice = dontCharge || !hasInterest ? rentalPrice : addMonetary(rentalPrice, interest);

    const isPeriodicContract = followUp.contract?.isNewLawContract ?? false;
    const rentUpdateNotice = calculateFollowUpRentUpdateNotice({
        isPeriodicContract,
        upgradeable: followUp.upgradeable,
        followUpMonth: followUp.month,
        followUpYear: followUp.year,
        initialDate: followUp.contract?.initialDate,
        durationMonths: followUp.contract?.durationMonths,
        updateFrequency: followUp.contract?.updateFrequency,
        rentalUpdates: followUp.contract?.rentalUpdates,
        contractCreatedAt: followUp.contract?.createdAt,
    });
    const upgradeable = followUp.upgradeable ?? false;
    // The single payment gate: a scheduled increase that still needs its price confirmed. priceJustUpdated
    // clears it, but ONLY after a full authoritative response passes validation, so the gate stays up
    // through every failed or malformed update — a fail-closed update never pretends the rent changed,
    // and the stale rent can never be paid until a fresh, trusted update actually clears it. This gate is
    // internal only: the view keys its update affordance off showUpdateAction, so it is never returned.
    const needsPriceUpdate = rentUpdateNotice.showUpdateAction && !priceJustUpdated;
    const paymentBlocked = needsPriceUpdate;
    /* Private Methods */

    // Return state and events
    return {
        form: formInstance,
        isInputDisabled,
        rentalForm,
        rentalPrice: realRentalPrice ?? 0,
        nextRentalPrice: followUp.nextPrice,
        paymentStatus: followUp.paymentStatus ?? '',
        upgradeable,
        showUpdateNotice: rentUpdateNotice.showUpdateNotice,
        showUpdateAction: rentUpdateNotice.showUpdateAction,
        isCurrentPeriodicFollowUp: rentUpdateNotice.isCurrentPeriodicFollowUp,
        amountPaid: followUp.amountPaid ?? 0,
        followUpStatus: followUp.followUpStatus ?? '',
        isUpdateModalFormVisible,
        isUpdateFormLoading,
        isPeriodicContract,
        adjustmentIndex: followUp.contract?.adjustmentIndex,
        onClickEditButton,
        onUpdateFormCancel,
        onUpdateFormSubmit,
        interestPrice: currencyFormatter(interest),
        charge: dontCharge,
        ignoreCharge,
        isOldFollowUp: rentUpdateNotice.isOldFollowUp,
        onChangeInput,
        valueInput,
        onBlurPayRent,
        onPaymentActionPointerDown,
        onPaymentActionPointerEnd,
        onEnterPayRent,
        onMarkAsPaid,
        onMarkAsUnpaid,
        hasInterest,
        isInterestModalVisible,
        onInterestModalCancel,
        onPayWithInterest,
        onIgnoreInterestAndPay,
    };
};
